<template>
	<el-dialog
		:visible.sync="dialogVisible"
		:destroy-on-close="true"
		append-to-body
		width="30%"
		top="15vh">
		<div class="d-flex mb-4">
			<h3 class="mr-auto ml-auto">{{ $t("title") }}</h3>
		</div>
		<div class="type-form">
			<label class="type-label">{{ $t("purchase_type") }}</label>
			<el-select
				v-model="selected"
				filterable
				:filter-method="onFilter"
				default-first-option
				class="w-100"
				:placeholder="$t('placeholder')">
				<el-option
					v-for="purchaseType in visibleTypes"
					:key="purchaseType.purchaseTypeId"
					:label="purchaseType.label"
					:value="purchaseType.purchaseTypeId" />
				<!-- Last, so Enter on a partial search picks the matching type rather than creating one. -->
				<el-option
					v-if="creatableName"
					:key="`new:${creatableName}`"
					:label="creatableName"
					:value="creatableName" />
			</el-select>
			<div
				v-if="newName"
				class="type-hint">
				{{ $t("will_be_created", { name: newName }) }}
			</div>
			<div
				v-else-if="typedDefault"
				class="type-hint">
				{{ $t("already_offered", { name: typedDefault.label }) }}
			</div>
		</div>
		<div class="d-flex justify-content-center mt-4 button-row">
			<el-button @click="dialogVisible = false">
				{{ $t("cancel") }}
			</el-button>
			<el-button
				type="success"
				plain
				:disabled="!resolved && !newName"
				@click="submit()">
				{{ resolved ? $t("use") : $t("create") }}
			</el-button>
		</div>
	</el-dialog>
</template>

<script>
import { TRANSLATION_KEY_BY_DEFAULT_TYPE_ID } from "@/pages/CampaignManager/FormItems/purchaseTypes";

// Picks a custom purchase type, or names a new one. The default types are not listed (the form's
// selector always offers them), but a typed name matching any type, default included (name or
// label, case-insensitive), resolves to that type, so no duplicate can be created.
export default {
	props: {
		value: Boolean,
		// All purchase types, each with its display `label`.
		purchaseTypes: {
			type: Array,
			default: () => []
		},
		initialName: {
			type: String,
			default: null
		}
	},
	data() {
		return {
			selected: null,
			query: ""
		};
	},
	computed: {
		dialogVisible: {
			get() {
				return this.value;
			},
			set(newValue) {
				this.$emit("input", newValue);
			}
		},
		// The search matches the label and the name ("sponsored" finds "Sponsorisé").
		visibleTypes() {
			const wanted = this.query.trim().toLowerCase();
			return this.purchaseTypes.filter(
				(purchaseType) => !TRANSLATION_KEY_BY_DEFAULT_TYPE_ID[purchaseType.purchaseTypeId] && (purchaseType.label.toLowerCase().includes(wanted) || purchaseType.name.toLowerCase().includes(wanted))
			);
		},
		resolved() {
			return this.purchaseTypes.find((purchaseType) => purchaseType.purchaseTypeId === this.selected) || this.findByName(this.selected);
		},
		newName() {
			return this.resolved ? null : (this.selected || "").trim() || null;
		},
		// A typed default type is not listed here: say where to find it.
		typedDefault() {
			const purchaseType = this.findByName(this.query);
			return purchaseType && TRANSLATION_KEY_BY_DEFAULT_TYPE_ID[purchaseType.purchaseTypeId] ? purchaseType : null;
		},
		// The typed name when it matches no type, else the pending new name, so it keeps its option.
		creatableName() {
			const typed = this.query.trim();
			return (typed && !this.findByName(typed) ? typed : null) || this.newName;
		}
	},
	watch: {
		value(visible) {
			if (visible) {
				this.selected = this.initialName;
				this.query = "";
			}
		}
	},
	methods: {
		onFilter(query) {
			this.query = query;
		},
		findByName(name) {
			const wanted = (name || "").trim().toLowerCase();
			return (wanted && this.purchaseTypes.find((purchaseType) => purchaseType.name.toLowerCase() === wanted || purchaseType.label.toLowerCase() === wanted)) || null;
		},
		submit() {
			if (this.resolved) {
				this.$emit("select", this.resolved);
			} else if (this.newName) {
				this.$emit("create", this.newName);
			}
		}
	}
};
</script>

<style scoped>
.type-form {
	width: 80%;
	margin: 0 auto;
	text-align: left;
}

.type-label {
	display: block;
	margin-bottom: 8px;
	font-size: 14px;
	color: #606266;
}

.type-hint {
	margin-top: 4px;
	color: #8492a6;
	font-size: 12px;
}

.button-row {
	gap: 12px;
}
</style>

<i18n>
{
  "en": {
    "title": "Add a purchase type",
    "purchase_type": "Purchase type",
    "placeholder": "Select or type a purchase type",
    "will_be_created": "\"{name}\" will be created on save",
    "already_offered": "\"{name}\" is already offered in the form",
    "cancel": "Cancel",
    "use": "Use",
    "create": "Create"
  },
  "fr": {
    "title": "Ajouter un type d'achat",
    "purchase_type": "Type d'achat",
    "placeholder": "Choisir ou saisir un type d'achat",
    "will_be_created": "« {name} » sera créé à l'enregistrement",
    "already_offered": "« {name} » est déjà proposé dans le formulaire",
    "cancel": "Annuler",
    "use": "Utiliser",
    "create": "Créer"
  },
  "de": {
    "title": "Kaufart hinzufügen",
    "purchase_type": "Kaufart",
    "placeholder": "Kaufart auswählen oder eingeben",
    "will_be_created": "„{name}\" wird beim Speichern erstellt",
    "already_offered": "„{name}\" ist bereits im Formular verfügbar",
    "cancel": "Abbrechen",
    "use": "Verwenden",
    "create": "Erstellen"
  }
}
</i18n>
